import type { Viewer } from '@vimeo/viewer';

/**
 * Returns the viewer's authentication status for analytics events.
 * 'logged_out' → not authenticated, 'free' → free/basic account, 'paid' → any paid plan.
 *
 * For team members, viewer.user.account is always 'basic' regardless of the team plan.
 * We use teamUser.accountType when present (the team owner's plan), falling back to
 * user.account for non-team users — the same pattern used in extractSafeViewerInfo
 * and Pendo initialisation throughout the codebase.
 */
export function deriveViewerAuthStatus(viewer: Viewer | null): 'logged_out' | 'free' | 'paid' {
  if (!viewer?.user) return 'logged_out';
  const effectiveAccount = viewer.teamUser?.accountType ?? viewer.user.account;
  return ['free', 'basic'].includes(effectiveAccount) ? 'free' : 'paid';
}

type ReferrerPage =
  | 'search'
  | 'clip_page'
  | 'user_profile'
  | 'homepage'
  | 'watchpage'
  | 'staff_picks'
  | 'category_page'
  | 'external'
  | 'unknown';

export function deriveReferrerPage(): ReferrerPage {
  if (typeof document === 'undefined' || !document.referrer) return 'unknown';
  try {
    const url = new URL(document.referrer);
    if (url.hostname !== 'vimeo.com' && !url.hostname.endsWith('.vimeo.com')) return 'external';
    const path = url.pathname;
    if (/^\/search/.test(path)) return 'search';
    if (/^\/staff_picks/.test(path)) return 'staff_picks';
    if (/^\/categories/.test(path)) return 'category_page';
    if (/^\/watch\/?$/.test(path)) return 'watchpage';
    if (path === '/') return 'homepage';
    if (/^\/home\/?$/.test(path)) return 'homepage';
    if (/^\/\d+\/?$/.test(path)) return 'clip_page';
    if (/^\/[^/]+\/?$/.test(path)) return 'user_profile';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

import type { PicoContextData, PicoIdentifiers } from './pico';

// If needed Omit properties from PicoIdentifiers or PicoContextData
export type SafeViewerInfo = PicoIdentifiers & PicoContextData;
/**
 * Extracts safe viewer info from a Viewer object.
 * Returns an object containing the necessary information to initialize PicoX.
 *
 * @example
 * const {
 *   viewerId, vuid, teamId, teamOwnerId, organizationId,
 *   accountType, isTeamUser, isFreeTrial, country, isMobile
 * } = extractSafeViewerInfo(viewer);
 */
export function extractSafeViewerInfo(viewer: Viewer | null): SafeViewerInfo {
  if (!viewer) {
    return {
      user_id: null,
      vuid: null,
      team_id: null,
      team_owner_id: null,
      organization_id: null,
      account_type: null,
      is_team_user: false,
      is_free_trial: false,
      country: null,
      is_mobile: false,
    };
  }

  const user_id = viewer.user?.id?.toString() ?? null;
  const vuid = viewer.vuid;
  const team_id = viewer.teamUser?.teamId?.toString() ?? null;
  const team_owner_id = viewer.teamUser?.ownerId?.toString() ?? null;
  const organization_id = viewer.user?.organizationId ?? null;
  const account_type =
    viewer.teamUser?.accountType?.toString() ?? viewer.user?.account?.toString() ?? null;
  const is_team_user = viewer.user?.isTeamUser ?? false;
  const is_free_trial = viewer.user?.isFreeTrial ?? false;
  const country = viewer.location;
  const is_mobile = viewer.isMobile;

  return {
    user_id,
    vuid,
    team_id,
    team_owner_id,
    organization_id,
    account_type,
    is_team_user,
    is_free_trial,
    country,
    is_mobile,
  };
}
